/**
 * InviteLandingPage — Public invitation acceptance (external / new-user path).
 *
 * Route: /invite/:inviteCode  (PUBLIC — no auth)
 * Onboarding Steps 1–3, Slice C. Design authority: SCOPING_ONBOARDING_FLOW
 * §7.4 (acceptance & account creation). Branded "Option A" layout (Skip,
 * 2026-07-06): Life4Earth frames the page; the org is the personal welcome.
 *
 * Flow:
 *   1. Validate the token (pre-auth GET /api/invitations/validate/{code}).
 *   2. Branded welcome panel (L4E header + one-line "what this is" + the org
 *      invitation + personal message + the org's home-community hero photo,
 *      falling back to the brand watermark) beside the account-creation form:
 *      email LOCKED to the invited address (§7.4 r2), name prefilled, password
 *      + confirm (live match check), DOB (16+ gate, §7.4 r1), the canonical
 *      home/operating geo declaration picker (home REQUIRED, §7.4 r4), and
 *      EULA / ToS / community-standards acks (§7.5).
 *   3. Submit → register-with-token (emailRegister carries inviteToken +
 *      communityStandardsAccepted). The register transaction consumes the
 *      invite, writes the lineage edge, and activates org membership.
 *   4. On success, land in the app (slice-2 landing walk resolves the geo
 *      landing; the wizard + org-About landing are the deferred Step 4).
 *
 * OAuth is intentionally cut for v1 (§7.4 r3) — email/password only.
 * Conventions: useAppTranslation, token colors only, logical properties.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useAuth } from '../../auth/AuthContext';
import { validateInviteCode } from '../../api/onboarding';
import type { InvitationResponse } from '../../api/onboarding';
import CommunityDeclarationFields from '../../Components/CommunityDeclaration/CommunityDeclarationFields';
import {
  fetchDeclarationPickerData,
  type DeclarationPickerData,
} from '../../Components/CommunityDeclaration/declarationTree';
import { mediaSrc } from '../../api/mediaUrl';
import l4eLogo from '../../assets/l4e-logo.svg';
import EulaModal from '../../Components/EulaModal/EulaModal';
import styles from './InviteLandingPage.module.css';

type PageState = 'loading' | 'ready' | 'expired' | 'error' | 'accepting';

const InviteLandingPage: React.FC = () => {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const { t } = useAppTranslation('pages');
  const { emailRegister } = useAuth();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [invitation, setInvitation] = useState<InvitationResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Geo declaration tree (pre-auth, geo_* only, inactive geos included).
  const [pickerData, setPickerData] = useState<DeclarationPickerData>({
    entities: [],
    relationships: [],
  });

  // Form state — single full-name field (the app's display identity is the
  // full name; first/last only feed DisplayName). One agreement checkbox
  // linking the EULA (register also records ToS acceptance alongside it).
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Age = two-tier self-attestation (Skip 2026-07-06). 16+ is the required
  // floor (§7.4 r1 / DEV-926); 18+ is optional and unlocks adult-only
  // surfaces. Translated to a synthetic birthdate on submit — the platform
  // derives all age gates from date_of_birth (self-attested, reasonable-
  // effort), so no backend age change is needed.
  const [is16Plus, setIs16Plus] = useState(false);
  const [is18Plus, setIs18Plus] = useState(false);
  const [homeCommunity, setHomeCommunity] = useState('');
  const [operatingCommunity, setOperatingCommunity] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [show18Help, setShow18Help] = useState(false);
  // Read-only EULA viewer. MUST be an in-page modal, never a link into
  // the app: this page is public, and navigating to an in-app route
  // boots the shell on whatever stale session the browser holds —
  // the invitee lands inside another user's logged-in account
  // (session-leak bug, 2026-07-08).
  const [showEula, setShowEula] = useState(false);

  /* ── Validate invite code + load the declaration tree on mount ── */
  useEffect(() => {
    if (!inviteCode) {
      setPageState('error');
      setErrorMessage(t('onboarding.invite.invalidCode'));
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const [inv, decl] = await Promise.all([
          validateInviteCode(inviteCode),
          fetchDeclarationPickerData(),
        ]);
        if (cancelled) return;

        setPickerData(decl);

        if (inv.status === 'expired' || inv.status === 'revoked') {
          setPageState('expired');
        } else if (inv.status === 'accepted') {
          navigate('/login', { replace: true });
        } else {
          setInvitation(inv);
          const nm = (inv.inviteeName || '').trim();
          if (nm) setFullName(nm);
          setPageState('ready');
        }
      } catch {
        if (!cancelled) {
          setPageState('error');
          setErrorMessage(t('onboarding.invite.validationFailed'));
        }
      }
    })();

    return () => { cancelled = true; };
  }, [inviteCode, navigate, t]);

  const pwFilled = !!password && !!confirmPassword;
  const pwMatch = pwFilled && password === confirmPassword;

  const canSubmit =
    pageState === 'ready' &&
    !!fullName.trim() &&
    !!password &&
    pwMatch &&
    is16Plus &&
    !!homeCommunity &&
    agreed;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setErrorMessage(null);
      if (!invitation || !inviteCode) return;
      if (password !== confirmPassword) {
        setErrorMessage(t('onboarding.invite.form.passwordMismatch'));
        return;
      }
      if (!homeCommunity) {
        setErrorMessage(t('onboarding.invite.form.homeRequired'));
        return;
      }

      setPageState('accepting');
      try {
        // Split the single name into first/last for the register DTO
        // (DisplayName is derived from them). One agreement records EULA +
        // ToS; community-standards ack is deferred (its rung gate is off).
        const parts = fullName.trim().split(/\s+/);
        // Translate the age attestation to a synthetic birthdate: 18 years
        // back when they attest adult, else 16 (the required floor). Every
        // platform age gate reads date_of_birth, so this makes the two-tier
        // attestation work everywhere without a schema change.
        const yearsBack = is18Plus ? 18 : 16;
        const nowD = new Date();
        const synthDob = new Date(nowD.getFullYear() - yearsBack, nowD.getMonth(), nowD.getDate())
          .toISOString()
          .split('T')[0];
        await emailRegister(invitation.inviteeEmail || '', password, {
          firstName: parts[0] || '',
          lastName: parts.slice(1).join(' '),
          dateOfBirth: synthDob,
          homeCommunityGuid: homeCommunity,
          operatingCommunityGuid: operatingCommunity || undefined,
          eulaAccepted: agreed,
          tosAccepted: agreed,
          communityStandardsAccepted: false,
          inviteToken: inviteCode,
        });
        // §7.6 r3 / §7.9 (LOCKED) — org-invited users land on the inviting
        // org's ABOUT page (orientation before activity); the welcome nudge
        // greets them there and points at the Action Center. Non-org invites
        // fall through to the normal landing walk.
        if (invitation.communityGuid) {
          navigate(`/communities/${invitation.communityGuid}#alignment`, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } catch (err: any) {
        setPageState('ready');
        setErrorMessage(err?.message || t('onboarding.invite.acceptFailed'));
      }
    },
    [
      invitation, inviteCode, password, confirmPassword, homeCommunity,
      operatingCommunity, fullName, is18Plus, agreed,
      emailRegister, navigate, t,
    ],
  );

  /* ── Simple states ── */

  if (pageState === 'loading') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.loadingText}>{t('onboarding.invite.loading')}</div>
        </div>
      </div>
    );
  }

  if (pageState === 'expired') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h1 className={styles.title}>{t('onboarding.invite.expiredTitle')}</h1>
          <p className={styles.body}>{t('onboarding.invite.expiredBody')}</p>
        </div>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h1 className={styles.title}>{t('onboarding.invite.errorTitle')}</h1>
          <p className={styles.body}>{errorMessage}</p>
        </div>
      </div>
    );
  }

  /* ── Ready / Accepting — the branded account-creation page ── */
  const accepting = pageState === 'accepting';
  const orgName = invitation?.communityName || '';
  const orgInitials = orgName
    ? orgName.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
    : 'L4';
  // Stored image values are inconsistent (bare object keys vs full URLs);
  // mediaSrc normalizes any form into a loadable src (or null → fallback).
  const heroUrl = mediaSrc(invitation?.communityHeroUrl) || '';
  const logoUrl = mediaSrc(invitation?.communityLogoUrl) || '';

  return (
    <div className={styles.container}>
      <div className={styles.brandCard}>
        {/* Brand header — Life4Earth + "Welcome" frame the whole page. */}
        <header className={styles.brandBar}>
          <div className={styles.brandLeft}>
            <img src={l4eLogo} alt="" className={styles.brandLogo} />
            <span className={styles.brandName}>Life4Earth</span>
            <span className={styles.brandWelcome}>{t('onboarding.invite.brand.invited')}</span>
          </div>
        </header>

        <div className={styles.split}>
          {/* Invitation hero panel (Revision B) — the org photo up top with a
              vignette fading into the panel, the logo badge overlapping its
              edge, then the org, the invitation sentence, and the note. */}
          <aside className={styles.welcome}>
            <div className={styles.heroWrap}>
              {heroUrl ? (
                <img src={heroUrl} alt={orgName} className={styles.heroImg} />
              ) : (
                /* Branded default band when the org has no hero photo. */
                <div className={styles.heroDefault} aria-hidden="true">
                  <svg viewBox="0 0 40 40" width="46" height="46">
                    <circle cx="20" cy="7" r="6" fill="currentColor" />
                    <circle cx="9" cy="30" r="6" fill="currentColor" />
                    <circle cx="31" cy="30" r="6" fill="currentColor" />
                    <circle cx="20" cy="20" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </div>
              )}
              <div className={styles.heroVignette} aria-hidden="true" />
              <div className={styles.heroFade} aria-hidden="true" />
            </div>

            {/* Logo badge, overlapping the hero's bottom edge. */}
            {logoUrl ? (
              <img src={logoUrl} alt={orgName} className={styles.orgBadgeImg} />
            ) : (
              <div className={styles.orgBadge} aria-hidden="true">{orgInitials}</div>
            )}

            <div className={styles.welcomeBody}>
              <p className={styles.orgName}>{orgName}</p>
              <p className={styles.invitationLine}>
                {t('onboarding.invite.invitationSentence', {
                  inviter: invitation?.inviterDisplayName,
                  org: orgName,
                })}
              </p>
              {invitation?.personalMessage && (
                <blockquote className={styles.quoteCard}>
                  {invitation.personalMessage}
                </blockquote>
              )}
            </div>

            <ul className={styles.welcomePoints}>
              <li className={styles.welcomePoint}>{t('onboarding.invite.point.quick')}</li>
              <li className={styles.welcomePoint}>
                {t('onboarding.invite.point.joinNowOrg', { org: orgName })}
              </li>
            </ul>
          </aside>

          {/* Account-creation form. */}
          <div className={styles.formPanel}>
            <h2 className={styles.formTitle}>{t('onboarding.invite.form.createTitle')}</h2>
            {errorMessage && <div className={styles.errorBanner}>{errorMessage}</div>}

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>{t('onboarding.invite.form.email')}</label>
                <div className={styles.readonlyField}>{invitation?.inviteeEmail}</div>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label}>{t('onboarding.invite.form.name')}</label>
                <input
                  className={styles.input}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="name"
                  required
                />
              </div>

              <div className={styles.row}>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>{t('onboarding.invite.form.password')}</label>
                  <input
                    type="password"
                    className={styles.input}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    minLength={8}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>{t('onboarding.invite.form.confirmPassword')}</label>
                  <input
                    type="password"
                    className={styles.input}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>
              {pwFilled && (
                <div className={pwMatch ? styles.pwMatch : styles.pwNoMatch}>
                  {pwMatch
                    ? t('onboarding.invite.form.passwordsMatch')
                    : t('onboarding.invite.form.passwordMismatch')}
                </div>
              )}

              <div className={styles.checkboxGroup}>
                <label className={styles.checkbox}>
                  <input type="checkbox" checked={is16Plus} onChange={(e) => setIs16Plus(e.target.checked)} required />
                  <span>{t('onboarding.invite.form.age16')}</span>
                </label>
                <div className={styles.checkboxRow}>
                  <label className={styles.checkbox}>
                    <input type="checkbox" checked={is18Plus} onChange={(e) => setIs18Plus(e.target.checked)} />
                    <span>{t('onboarding.invite.form.age18')}</span>
                  </label>
                  <span className={styles.helpWrap}>
                    <button
                      type="button"
                      className={styles.helpBtn}
                      aria-label={t('onboarding.invite.form.moreInfo')}
                      aria-expanded={show18Help}
                      onClick={() => setShow18Help((v) => !v)}
                    >
                      ?
                    </button>
                    {show18Help && (
                      <span className={styles.helpPopover} role="tooltip">
                        {t('onboarding.invite.form.age18Help')}
                      </span>
                    )}
                  </span>
                </div>
              </div>

              <CommunityDeclarationFields
                entities={pickerData.entities}
                relationships={pickerData.relationships}
                primaryValue={homeCommunity}
                onPrimaryChange={setHomeCommunity}
                operatingValue={operatingCommunity}
                onOperatingChange={setOperatingCommunity}
                primaryLabel={t('onboarding.invite.form.homeCommunity')}
                primaryHint={t('onboarding.invite.form.homeHelp')}
                operatingLabel={t('onboarding.invite.form.operatingCommunity')}
                operatingHint={t('onboarding.invite.form.operatingHint')}
                placeholder={t('onboarding.invite.form.homePlaceholder')}
                showOperating={false}
              />

              <div className={styles.checkboxGroup}>
                <label className={styles.checkbox}>
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} required />
                  <span>
                    {t('onboarding.invite.form.agreeText')}{' '}
                    <button
                      type="button"
                      className={styles.agreeLink}
                      onClick={(e) => {
                        // Prevent the surrounding <label> from toggling
                        // the checkbox when the link-button is clicked.
                        e.preventDefault();
                        setShowEula(true);
                      }}
                    >
                      {t('onboarding.invite.form.eulaLink')}
                    </button>
                    .
                  </span>
                </label>
              </div>

              <button type="submit" className={styles.submitBtn} disabled={!canSubmit || accepting}>
                {accepting
                  ? t('onboarding.invite.accepting')
                  : t('onboarding.invite.form.submitFull')}
              </button>
              {!canSubmit && !accepting && (
                <p className={styles.submitHint}>{t('onboarding.invite.form.submitHint')}</p>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Read-only EULA viewer — acceptance happens via the checkbox +
          register call, not here. */}
      <EulaModal
        open={showEula}
        viewOnly
        onAccepted={() => {}}
        onAbort={() => setShowEula(false)}
      />
    </div>
  );
};

export default InviteLandingPage;
